import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Globe, RefreshCw, Edit3, Save, X, Copy, Languages } from 'lucide-react';
import TranslationService from '../services/translationService';
import GoogleTranslateService from '../services/googleTranslateService';
import LibreTranslateService from '../services/libreTranslateService';
import TranslationMethodDialog from './TranslationMethodDialog';
import TranscriptionDialog from './TranscriptionDialog';
import { useSettings } from '../context/SettingsContext';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const LanguageButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 2px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 6px;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.active ? props.theme.primary + 'dd' : props.theme.primary + '08'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Context Menu for segments
const ContextMenu = styled.div`
  position: fixed;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 4px 0;
  min-width: 180px;
`;

const ContextMenuItem = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: ${props => props.theme.text};
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LanguageBadge = styled.span`
  background: ${props => 
    props.isOriginal ? '#28a745' : 
    props.isTranslated ? '#007bff' : '#6c757d'
  };
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
`;

// New segment language badges
const SegmentLanguageBadges = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const SegmentLanguageBadge = styled.button`
  padding: 2px 6px;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 10px;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 2px;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.active ? props.theme.primary + 'dd' : props.theme.primary + '08'};
  }

  svg {
    width: 10px;
    height: 10px;
  }
`;

const TranslationContainer = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
`;

const Panel = styled.div`
  flex: 1;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  padding: 12px 16px;
  background: ${props => props.theme.surface};
  border-bottom: 1px solid ${props => props.theme.border};
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const TranslationBlock = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 6px;
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
`;

const BlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const BlockTime = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  font-weight: 500;
`;

const BlockActions = styled.div`
  display: flex;
  gap: 4px;
`;

const BlockActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
    color: ${props => props.theme.text};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const BlockText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${props => props.theme.text};
  user-select: text;
  cursor: text;
`;

const EditableText = styled.textarea`
  width: 100%;
  min-height: 60px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: ${props => props.theme.text};
  background: ${props => props.theme.background};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const EditButton = styled.button`
  padding: 4px 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const EmptyTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  margin: 0 0 16px 0;
  max-width: 300px;
`;

const TranslationPanel = ({ project, onUpdate }) => {
  const [editingBlock, setEditingBlock] = useState(null);
  const [editText, setEditText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);

  // Language switching and display
  const [displayLanguage, setDisplayLanguage] = useState('original'); // 'original' or specific language code
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [segmentLanguages, setSegmentLanguages] = useState({}); // Track per-segment language
  
  // Method selection dialog
  const [showMethodDialog, setShowMethodDialog] = useState(false);
  
  // Dialog states for better UX
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    selectableText: '',
    showProgress: false,
    progress: 0,
    progressText: ''
  });

  const settings = useSettings();
  const apiKeys = settings?.apiKeys || {};
  const transcript = project.transcript || [];
  
  // Support both old and new translation format
  const translations = project.translations || {};
  
  // Legacy support: if old translation exists, convert it
  if (project.translation && project.translationLanguage && !translations[project.translationLanguage]) {
    translations[project.translationLanguage] = project.translation;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTranslate = async () => {
    if (transcript.length === 0) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'No Transcript Available',
        message: 'Please transcribe the video first before translating.',
        selectableText: '',
        showProgress: false
      });
      return;
    }

    // Show method selection dialog with language selection
    setShowMethodDialog(true);
  };

  // Handle method selection and start translation
  const handleMethodSelection = async (methodId, methodData, selectedLanguage) => {
    setShowMethodDialog(false);
    
    if (methodId === 'deepl-api') {
      await handleDeepLTranslation(selectedLanguage);
    } else if (methodId === 'google-free') {
      await handleGoogleFreeTranslation(selectedLanguage);
    } else if (methodId === 'local-libre') {
      await handleLibreTranslation(selectedLanguage);
    } else {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Method Not Implemented',
        message: `${methodData.name} is not yet implemented. Please choose a different method.`,
        selectableText: '',
        showProgress: false
      });
    }
  };

  // Handle DeepL API translation
  const handleDeepLTranslation = async (targetLanguage) => {
    if (!apiKeys.deepL) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'DeepL API Key Required',
        message: 'Please configure your DeepL API key in Settings to use DeepL translation.',
        selectableText: `DeepL Translation Requirements:\n- DeepL API Key: ${apiKeys.deepL ? '✓ Configured' : '✗ Missing'}\n\nGet your free API key at: https://www.deepl.com/pro-api`,
        showProgress: false
      });
      return;
    }

    setIsTranslating(true);
    
    try {
      const translationService = new TranslationService(apiKeys);
      
      // Show progress dialog
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Translating with DeepL API',
        message: `Translating ${transcript.length} segments to ${targetLanguage}...`,
        selectableText: '',
        showProgress: true,
        progress: 10,
        progressText: 'Starting DeepL translation...'
      });

      const result = await translationService.translateSegments(
        transcript, 
        targetLanguage, 
        project.transcriptionLanguage || 'en', // Use source language instead of auto
        (progress, progressText) => {
          setDialog(prev => ({
            ...prev,
            progress,
            progressText
          }));
        }
      );

      if (result.success) {
        // Check if any translations actually worked
        const successfulTranslations = result.translatedSegments.filter(seg => 
          !seg.translationError && seg.text !== seg.originalText
        );
        
        if (successfulTranslations.length === 0) {
          throw new Error('All translations failed - no segments were translated successfully');
        }

        // Update with multiple translations support
        const updatedTranslations = { ...translations };
        updatedTranslations[targetLanguage] = result.translatedSegments;

        onUpdate({
          translations: updatedTranslations,
          // Keep legacy support
          translation: result.translatedSegments,
          translationLanguage: targetLanguage,
          status: 'translated'
        });

        // Switch to newly translated language
        setDisplayLanguage(targetLanguage);

        const failedCount = result.totalSegments - successfulTranslations.length;
        const successMessage = failedCount > 0 
          ? `Translated ${successfulTranslations.length}/${result.totalSegments} segments (${failedCount} failed)`
          : `Successfully translated all ${result.totalSegments} segments`;

        setDialog({
          isOpen: true,
          type: failedCount > 0 ? 'warning' : 'success',
          title: 'DeepL Translation Complete',
          message: `${successMessage} to ${targetLanguage}!`,
          selectableText: `Translation Details:\n- Service: DeepL API\n- Successful: ${successfulTranslations.length}/${result.totalSegments}\n- Target Language: ${targetLanguage}\n- Source Language: ${result.sourceLanguage}\n- Quality: Professional${failedCount > 0 ? `\n- Note: ${failedCount} segments failed, you may need to edit them manually` : ''}`,
          showProgress: false
        });
      } else {
        throw new Error('DeepL translation failed');
      }
    } catch (error) {
      console.error('DeepL translation error:', error);
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'DeepL Translation Failed',
        message: 'An error occurred during DeepL translation.',
        selectableText: `Error Details:\n${error.message}\n\nTroubleshooting:\n1. Check your DeepL API key in Settings\n2. Verify you have enough quota remaining\n3. Check your internet connection\n4. Try the free Google Translate option instead`,
        showProgress: false
      });
    } finally {
      setIsTranslating(false);
    }
  };

  // Handle Google Free translation
  const handleGoogleFreeTranslation = async (targetLanguage) => {
    setIsTranslating(true);
    
    try {
      const googleService = new GoogleTranslateService();
      
      // Show progress dialog
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Translating with Google (Free)',
        message: `Translating ${transcript.length} segments to ${targetLanguage}...`,
        selectableText: '',
        showProgress: true,
        progress: 10,
        progressText: 'Starting free Google translation...'
      });

      const result = await googleService.translateSegments(
        transcript, 
        targetLanguage, 
        project.transcriptionLanguage || 'en', // Use source language instead of auto
        (progress, progressText) => {
          setDialog(prev => ({
            ...prev,
            progress,
            progressText
          }));
        }
      );

      if (result.success) {
        // Check if any translations actually worked
        const successfulTranslations = result.translatedSegments.filter(seg => 
          !seg.translationError && seg.text !== seg.originalText
        );
        
        if (successfulTranslations.length === 0) {
          throw new Error('All translations failed - no segments were translated successfully');
        }

        // Update with multiple translations support
        const updatedTranslations = { ...translations };
        updatedTranslations[targetLanguage] = result.translatedSegments;

      onUpdate({
          translations: updatedTranslations,
          // Keep legacy support
          translation: result.translatedSegments,
        translationLanguage: targetLanguage,
        status: 'translated'
        });

        // Switch to newly translated language
        setDisplayLanguage(targetLanguage);

        const failedCount = result.totalSegments - successfulTranslations.length;
        const successMessage = failedCount > 0 
          ? `Translated ${successfulTranslations.length}/${result.totalSegments} segments (${failedCount} failed)`
          : `Successfully translated all ${result.totalSegments} segments`;

        setDialog({
          isOpen: true,
          type: failedCount > 0 ? 'warning' : 'success',
          title: 'Free Translation Complete',
          message: `${successMessage} to ${targetLanguage} for FREE!`,
          selectableText: `Translation Details:\n- Service: Google Translate (Free)\n- Successful: ${successfulTranslations.length}/${result.totalSegments}\n- Target Language: ${targetLanguage}\n- Source Language: ${result.sourceLanguage}\n- Cost: FREE!${failedCount > 0 ? `\n- Note: ${failedCount} segments failed, you may need to edit them manually` : ''}`,
          showProgress: false
        });
      } else {
        throw new Error('Google translation failed');
      }
    } catch (error) {
      console.error('Google translation error:', error);
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Free Translation Failed',
        message: 'An error occurred during free Google translation.',
        selectableText: `Error Details:\n${error.message}\n\nTroubleshooting:\n1. Check your internet connection\n2. Try smaller batches (segments)\n3. Wait a moment and try again\n4. Consider using DeepL API for higher reliability`,
        showProgress: false
      });
    } finally {
      setIsTranslating(false);
    }
  };

  // Handle LibreTranslate (Local/Public)
  const handleLibreTranslation = async (targetLanguage) => {
    setIsTranslating(true);
    
    try {
      const libreService = new LibreTranslateService();
      
      // Check if LibreTranslate is available
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Checking LibreTranslate Setup',
        message: 'Looking for local or public LibreTranslate instances...',
        selectableText: '',
        showProgress: true,
        progress: 20,
        progressText: 'Checking connections...'
      });

      const setupCheck = await libreService.checkLocalSetup();
      
      if (!setupCheck.url) {
        const instructions = libreService.getSetupInstructions();
        setDialog({
          isOpen: true,
          type: 'warning',
          title: 'LibreTranslate Setup Required',
          message: setupCheck.error || 'LibreTranslate is not available.',
          selectableText: `${instructions.title}\n\n${instructions.description}\n\nSetup Options:\n\n` +
            instructions.options.map((option, i) => 
              `Option ${i + 1}: ${option.method}\n${option.steps.join('\n')}\nRequirements: ${option.requirements.join(', ')}\n`
            ).join('\n'),
          showProgress: false
        });
        return;
      }

      // Update service URL
      libreService.setApiUrl(setupCheck.url);
      
      // Show progress dialog
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Translating with LibreTranslate',
        message: `Translating ${transcript.length} segments to ${targetLanguage}...`,
        selectableText: setupCheck.warning ? `Warning: ${setupCheck.warning}` : setupCheck.message,
        showProgress: true,
        progress: 10,
        progressText: 'Starting LibreTranslate translation...'
      });

      const result = await libreService.translateSegments(
        transcript, 
        targetLanguage, 
        project.transcriptionLanguage || 'en', // Use source language instead of auto
        (progress, progressText) => {
          setDialog(prev => ({
            ...prev,
            progress,
            progressText
          }));
        }
      );

      if (result.success) {
        // Check if any translations actually worked
        const successfulTranslations = result.translatedSegments.filter(seg => 
          !seg.translationError && seg.text !== seg.originalText
        );
        
        if (successfulTranslations.length === 0) {
          throw new Error('All translations failed - no segments were translated successfully');
        }

        // Update with multiple translations support
        const updatedTranslations = { ...translations };
        updatedTranslations[targetLanguage] = result.translatedSegments;

        onUpdate({
          translations: updatedTranslations,
          // Keep legacy support
          translation: result.translatedSegments,
          translationLanguage: targetLanguage,
          status: 'translated'
        });

        // Switch to newly translated language
        setDisplayLanguage(targetLanguage);

        const failedCount = result.totalSegments - successfulTranslations.length;
        const successMessage = failedCount > 0 
          ? `Translated ${successfulTranslations.length}/${result.totalSegments} segments (${failedCount} failed)`
          : `Successfully translated all ${result.totalSegments} segments`;

        setDialog({
          isOpen: true,
          type: failedCount > 0 ? 'warning' : 'success',
          title: 'LibreTranslate Complete',
          message: `${successMessage} to ${targetLanguage} using LibreTranslate!`,
          selectableText: `Translation Details:\n- Service: LibreTranslate (${setupCheck.hasLocal ? 'Local' : 'Public'})\n- Successful: ${successfulTranslations.length}/${result.totalSegments}\n- Target Language: ${targetLanguage}\n- Source Language: ${result.sourceLanguage}\n- Cost: FREE!\n- Privacy: ${setupCheck.hasLocal ? 'Private (Local)' : 'Public Instance'}${failedCount > 0 ? `\n- Note: ${failedCount} segments failed, you may need to edit them manually` : ''}`,
          showProgress: false
        });
      } else {
        throw new Error('LibreTranslate translation failed');
      }
    } catch (error) {
      console.error('LibreTranslate translation error:', error);
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'LibreTranslate Failed',
        message: 'An error occurred during LibreTranslate translation.',
        selectableText: `Error Details:\n${error.message}\n\nTroubleshooting:\n1. Check if LibreTranslate is running locally\n2. Install Docker and run: docker run -ti --rm -p 5000:5000 libretranslate/libretranslate\n3. Check your internet connection for public instances\n4. Try Google Translate (Free) as an alternative`,
        showProgress: false
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const handleEditBlock = (block) => {
    setEditingBlock(block.id);
    setEditText(block.text);
  };

  const handleSaveEdit = () => {
    const currentSegmentLanguage = segmentLanguages[editingBlock] || displayLanguage;
    
    if (currentSegmentLanguage === 'original') {
      const updatedSegments = transcript.map(block =>
        block.id === editingBlock ? { ...block, text: editText } : block
      );
      onUpdate({ transcript: updatedSegments });
    } else {
      const updatedSegments = translations[currentSegmentLanguage].map(block =>
      block.id === editingBlock ? { ...block, text: editText } : block
    );
      const updatedTranslations = { ...translations };
      updatedTranslations[currentSegmentLanguage] = updatedSegments;
      onUpdate({ 
        translations: updatedTranslations,
        // Keep legacy support
        translation: updatedSegments,
        translationLanguage: currentSegmentLanguage
      });
    }
    
    setEditingBlock(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingBlock(null);
    setEditText('');
  };

  const handleCopyTranslation = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Handle text selection for translation
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    setSelectedText(selectedText);
  };

  // Handle right-click context menu
  const handleContextMenu = (e, block) => {
    e.preventDefault();
    handleTextSelection(); // Check for selected text
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      block: block,
      hasSelection: selectedText.length > 0
    });
  };

  // Handle segment language change
  const handleSegmentLanguageChange = (blockId, languageCode) => {
    setSegmentLanguages(prev => ({
      ...prev,
      [blockId]: languageCode
    }));
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
      setSelectedText('');
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Get display segments based on current language selection
  const getDisplaySegments = () => {
    if (displayLanguage === 'original') {
      return transcript;
    }
    return translations[displayLanguage] || [];
  };

  // Get segment text based on individual segment language
  const getSegmentText = (block) => {
    const segmentLanguage = segmentLanguages[block.id] || displayLanguage;
    
    if (segmentLanguage === 'original') {
      return transcript.find(t => t.id === block.id)?.text || block.text;
    }
    
    if (translations[segmentLanguage]) {
      return translations[segmentLanguage].find(t => t.id === block.id)?.text || block.text;
    }
    
    return block.text;
  };

  // Get available languages for display - improved to show all translations
  const getAvailableLanguages = () => {
    const languages = [];
    
    // Original language
    if (transcript.length > 0) {
      languages.push({
        code: 'original',
        name: project.transcriptionLanguage || 'English',
        type: 'transcript',
        icon: <Languages />
      });
    }
    
    // All translation languages
    Object.keys(translations).forEach(languageCode => {
      if (translations[languageCode] && translations[languageCode].length > 0) {
        // Get language name from Google Translate service
        const googleService = new GoogleTranslateService();
        const supportedLanguages = googleService.getSupportedLanguages();
        const languageInfo = supportedLanguages.find(l => l.code === languageCode);
        
        languages.push({
          code: languageCode,
          name: languageInfo ? languageInfo.name : languageCode.toUpperCase(),
          type: 'translation',
          icon: <Globe />
        });
      }
    });
    
    return languages;
  };

  const availableLanguages = getAvailableLanguages();
  const displaySegments = getDisplaySegments();
  const currentLanguage = availableLanguages.find(lang => lang.code === displayLanguage);

  return (
    <Container>
      <Header>
        <Title>
          <Languages />
          Translation
          {currentLanguage && (
            <LanguageBadge 
              isOriginal={displayLanguage === 'original'} 
              isTranslated={displayLanguage !== 'original'}
            >
              {currentLanguage.name}
            </LanguageBadge>
          )}
        </Title>
        <Controls>
          {/* Language Selection Buttons - Show All Available Languages */}
          {availableLanguages.length > 1 && (
            <LanguageButtons>
              {availableLanguages.map((language) => (
                <LanguageButton
                  key={language.code}
                  active={displayLanguage === language.code}
                  onClick={() => setDisplayLanguage(language.code)}
                >
                  {language.icon}
                  {language.name}
                </LanguageButton>
              ))}
            </LanguageButtons>
          )}
          
          {/* Clear All Translations Button */}
          {Object.keys(translations).length > 0 && (
            <ActionButton 
              onClick={() => {
                onUpdate({ 
                  translations: {}, 
                  translation: [], 
                  translationLanguage: null, 
                  status: 'transcribed' 
                });
                setDisplayLanguage('original');
                setSegmentLanguages({});
              }}
            >
              <X />
              Clear All Translations
            </ActionButton>
          )}
          
          {/* Translate Button */}
          <ActionButton 
            variant="primary" 
            onClick={handleTranslate}
            disabled={isTranslating || transcript.length === 0}
          >
            <Globe />
            {isTranslating ? 'Translating...' : 'Translate'}
          </ActionButton>
        </Controls>
      </Header>

      {transcript.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Globe size={24} color="#6c757d" />
          </EmptyIcon>
          <EmptyTitle>No Transcript Available</EmptyTitle>
          <EmptyDescription>
            You need to transcribe the video first before you can translate it.
          </EmptyDescription>
        </EmptyState>
      ) : (
        <TranslationContainer>
          <Panel>
            <PanelHeader>
              {currentLanguage ? `${currentLanguage.name} (${currentLanguage.type === 'transcript' ? 'Original' : 'Translated'})` : 'Transcript'}
            </PanelHeader>
            <PanelContent>
              {displaySegments.length === 0 ? (
                <EmptyState>
                  <EmptyIcon>
                    <Globe size={24} color="#6c757d" />
                  </EmptyIcon>
                  <EmptyTitle>
                    {displayLanguage !== 'original' ? 'No Translation Available' : 'No Transcript Available'}
                  </EmptyTitle>
                  <EmptyDescription>
                    {displayLanguage !== 'original' 
                      ? 'Click "Translate" to generate translations for your transcript.'
                      : 'Please transcribe the video first.'
                    }
                  </EmptyDescription>
                </EmptyState>
              ) : (
                displaySegments.map((block) => {
                  const segmentLanguage = segmentLanguages[block.id] || displayLanguage;
                  const segmentText = getSegmentText(block);
                  
                  return (
                    <TranslationBlock 
                      key={`display-${block.id}`}
                      onContextMenu={(e) => handleContextMenu(e, block)}
                    >
                    <BlockHeader>
                      <BlockTime>
                        {formatTime(block.startTime)} - {formatTime(block.endTime)}
                          <LanguageBadge 
                            isOriginal={segmentLanguage === 'original'} 
                            isTranslated={segmentLanguage !== 'original'}
                          >
                            {availableLanguages.find(l => l.code === segmentLanguage)?.name || 'Unknown'}
                          </LanguageBadge>
                          {block.translationError && (
                            <span style={{ color: '#dc3545', fontSize: '10px', marginLeft: '8px' }}>
                              ⚠️ Translation Error
                            </span>
                          )}
                      </BlockTime>
                      <BlockActions>
                          <BlockActionButton onClick={() => handleCopyTranslation(segmentText)}>
                          <Copy />
                        </BlockActionButton>
                        <BlockActionButton onClick={() => handleEditBlock(block)}>
                          <Edit3 />
                        </BlockActionButton>
                      </BlockActions>
                    </BlockHeader>

                    {editingBlock === block.id ? (
                      <div>
                        <EditableText
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          autoFocus
                        />
                        <EditActions>
                          <EditButton variant="primary" onClick={handleSaveEdit}>
                            <Save />
                            Save
                          </EditButton>
                          <EditButton onClick={handleCancelEdit}>
                            <X />
                            Cancel
                          </EditButton>
                        </EditActions>
                      </div>
                    ) : (
                        <div>
                          <BlockText 
                            style={{
                              color: block.translationError ? '#6c757d' : 'inherit',
                              fontStyle: block.translationError ? 'italic' : 'normal'
                            }}
                            onMouseUp={handleTextSelection}
                          >
                            {segmentText}
                          </BlockText>
                          
                          {/* Individual Segment Language Badges */}
                          {availableLanguages.length > 1 && (
                            <SegmentLanguageBadges>
                              {availableLanguages.map((language) => (
                                <SegmentLanguageBadge
                                  key={language.code}
                                  active={segmentLanguage === language.code}
                                  onClick={() => handleSegmentLanguageChange(block.id, language.code)}
                                >
                                  {language.icon}
                                  {language.name}
                                </SegmentLanguageBadge>
                              ))}
                            </SegmentLanguageBadges>
                          )}
                          
                          {block.translationError && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#dc3545', 
                              marginTop: '4px',
                              fontStyle: 'italic'
                            }}>
                              Error: {block.translationError}
                            </div>
                          )}
                          {block.originalText && block.text !== block.originalText && segmentLanguage !== 'original' && (
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#28a745', 
                              marginTop: '4px',
                              fontStyle: 'italic'
                            }}>
                              ✅ Translated from: "{block.originalText.substring(0, 50)}${block.originalText.length > 50 ? '...' : ''}"
                            </div>
                          )}
                        </div>
                    )}
                  </TranslationBlock>
                  );
                })
              )}
            </PanelContent>
          </Panel>

          {/* Context Menu */}
          {contextMenu && (
            <ContextMenu style={{ left: contextMenu.x, top: contextMenu.y }}>
              {/* Text selection translation option */}
              {contextMenu.hasSelection && selectedText && (
                <>
                  <ContextMenuItem onClick={() => {
                    // Handle selected text translation
                    setContextMenu(null);
                    setShowMethodDialog(true);
                  }}>
                    <Globe />
                    Translate Selected Text
                  </ContextMenuItem>
                  <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)' }} />
                </>
              )}
              
              {/* Language switching options */}
              {availableLanguages.map((language) => (
                <ContextMenuItem 
                  key={language.code}
                  onClick={() => {
                    handleSegmentLanguageChange(contextMenu.block.id, language.code);
                    setContextMenu(null);
                  }}
                >
                  {language.icon}
                  Show {language.name}
                </ContextMenuItem>
              ))}
              
              <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)' }} />
              
              <ContextMenuItem onClick={() => {
                handleCopyTranslation(getSegmentText(contextMenu.block));
                setContextMenu(null);
              }}>
                <Copy />
                Copy Text
              </ContextMenuItem>
              <ContextMenuItem onClick={() => {
                handleEditBlock(contextMenu.block);
                setContextMenu(null);
              }}>
                <Edit3 />
                Edit Text
              </ContextMenuItem>
            </ContextMenu>
          )}
        </TranslationContainer>
      )}

      {/* Method Selection Dialog */}
      <TranslationMethodDialog
        isOpen={showMethodDialog}
        onClose={() => setShowMethodDialog(false)}
        onSelectMethod={handleMethodSelection}
        availableMethods={apiKeys}
        sourceLanguage={project.transcriptionLanguage || "English"}
        hasExistingTranslation={Object.keys(translations).length > 0}
      />

      {/* Translation Dialog */}
      <TranscriptionDialog
        isOpen={dialog.isOpen}
        onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        selectableText={dialog.selectableText}
        showProgress={dialog.showProgress}
        progress={dialog.progress}
        progressText={dialog.progressText}
        onOpenSettings={() => {
          setDialog(prev => ({ ...prev, isOpen: false }));
        }}
        onRetry={() => {
          setDialog(prev => ({ ...prev, isOpen: false }));
          handleTranslate();
        }}
      />
    </Container>
  );
};

export default TranslationPanel;